import { Grid, Typography } from '@mui/material';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, format } from 'date-fns';
import BookingTypeList from '../month/bookingTypeList';
import { useCalendar } from '../calendarContext'; 
import { useCalendarViews } from '../view/calendarViewsProvider';
import { useMemo } from 'react';

const RenderDays = ({ month, showEventDetailsInside, showEventMarker, mHeight, openings }) => {
    const {
        selectedDate, 
        toggleDate,
    } = useCalendar();
    const {
        setView,
        view
    } = useCalendarViews() 

    const openingsByDate = useMemo(() => {
        if (!openings) return {};
        return openings.reduce((acc, curr) => {
            acc[curr.date] = curr.available.toLowerCase() === "yes";
            return acc;
        }, {});
    }, [openings]);


    const startDate = startOfMonth(month); // First day of the month
    const endDate = endOfMonth(month);     // Last day of the month
    const startOfWeekDate = startOfWeek(startDate); // First day of the week (Sunday)
    const endOfWeekDate = endOfWeek(endDate); // Last day of the week (Saturday)

    const days = eachDayOfInterval({ start: startOfWeekDate, end: endOfWeekDate });
    const sortedDays = days.sort((a, b) => a.getTime() - b.getTime()); // Sort days in ascending order

    // Combine the real days with the empty cells to make a full 6 rows
    const allDays = [...sortedDays];
    // Format the date for comparison.

    return (
        <Grid container >
            {/* Render Weekday Names */}
            <Grid container>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <Grid item xs={12 / 7} key={index} align="center">
                        <Typography variant="body2" fontWeight="bold">{day}</Typography>
                    </Grid>
                ))}
            </Grid>

            {/* Render the Days in the Month */}
            {allDays.map((day, ind) => {
                const formattedDate = format(day, 'yyyy-MM-dd');
                const isDayOpen = openings ? (!!openingsByDate[formattedDate]) : true;
                const isInCurrentMonth = day && isSameMonth(day, month); // Check if day is in the current month
                if (!isInCurrentMonth && !isDayOpen) {
                    return (
                        <Grid
                            item
                            key={ind}
                            xs={12 / 7}
                            sx={{ 
                                boxSizing: 'border-box',
                                padding: '4px',
                                aspectRatio: '1',
                                height: mHeight ?? 'auto',
                            }}
                        />
                    );
                }

                const isSelectedDay = day && selectedDate && isSameDay(day, selectedDate); // Check if the day is selected
                const isTodayDay = day && isToday(day); // Check if the day is today

                function getBackgroundColor(isSelectedDay, isInCurrentMonth, isDayOpen) {
                    if (isSelectedDay) {
                        return 'lightblue';
                    } else if (isInCurrentMonth) { 
                        return isDayOpen ? '#fff' : '#f9f9f9';
                    } else {
                        return '#f9f9f9';
                    }
                }

                return (
                    <Grid
                        item
                        key={ind}
                        xs={12 / 7} // Ensure 7 equal columns for each day
                        onClick={(e) => {
                            if (!isDayOpen) return;
                            e.stopPropagation();
                            setView && setView(view === "month" || view === "year" ? "day" : view);
                            toggleDate(day);
                        }}
                        sx={{
                            textAlign: 'center',
                            background: getBackgroundColor(isSelectedDay, isInCurrentMonth, isDayOpen),
                            opacity: isDayOpen ? 1 : 0.3, // Lower opacity for grayed-out, non-available days
                            cursor: isDayOpen ? 'pointer' : 'not-allowed',
                            pointerEvents: isDayOpen ? 'auto' : 'none',
                            border: '1px solid #e0e0e0', // Subtle border around each cell
                            padding: '4px', // Padding for better spacing within cells
                            boxSizing: 'border-box', // Ensures padding doesn't affect the overall width
                            aspectRatio: '1', // Ensures equal width and height (square cells)
                            height: mHeight ?? 'auto', // Ensure height adjusts based on width
                            borderRadius: '6px', // Rounded corners for a softer look
                            transition: 'background 0.2s, transform 0.2s', // Smooth hover effect
                        }}
                    >
                        {day ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start', // Align content to the top
                                    alignItems: 'center', // Centers content horizontally
                                    height: '100%', // Full height of the cell
                                    width: '100%', // Full width of the cell
                                    boxSizing: 'border-box', // Ensures padding doesn't affect size

                                }}
                            >
                                {/* Day Number at the Top Center */}
                                <Typography
                                    variant="body1"
                                    style={{
                                        background: isTodayDay ? '#1a73e8' : 'transparent', // Highlight today
                                        color: isTodayDay ? '#fff' : isSelectedDay ? '#fff' : '#333', // Text color for selected/today
                                        width: '25px', // Set width for the day circle
                                        height: '25px', // Set height for the day circle
                                        borderRadius: '50%', // Makes the day number circular
                                        display: 'flex',
                                        alignItems: 'center', // Vertically center the text inside the circle
                                        justifyContent: 'center', // Horizontally center the text inside the circle
                                        fontWeight: isTodayDay ? 'bold' : 'normal', // Bold font for today
                                        marginBottom: '4px', // Space between day number and event list
                                        boxSizing: 'border-box', // Ensures that padding does not affect width/height
                                    }}
                                >
                                    {day.getDate()}
                                </Typography>


                                {/* Event List Below the Day Number */}
                                <div style={{ width: '100%', textAlign: 'center' }}>
                                    <BookingTypeList
                                        date={day}
                                        showEventDetails={showEventDetailsInside}
                                        showEventMarker={showEventMarker} // or false, depending on your needs
                                        day={day}
                                        month={month} 
                                    /> 
                                </div>
                            </div>


                        ) : null} {/* Empty cell for non-month days */}
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default RenderDays;
